import { SAJU_PRODUCT_COPY } from './template-saju-copy.mjs';

const ASSET_ROOT = '/images/content-templates/editorial';
const FONT = 'Content KoPub Dotum';
const PAGE = { width: 760, height: 1078 };
const INK = '#193b46';
const TEAL = '#237e79';
const CORAL = '#e78b75';
const NAVY = '#142739';
const GOLD = '#d8b56e';
const CREAM = '#fbf6e9';

// The scene is the editable source: text, geometry and project artwork remain
// separate objects when the native editor imports each page.
function page(id, title, background) {
  const frameId = `${id}-frame`;
  const scene = { version: 1, title, elements: [
    { id: frameId, type: 'frame', title, x: 0, y: 0, ...PAGE, fillColor: background },
  ], assets: [] };
  const add = (key, element) => scene.elements.push({ id: `${id}-${key}`, parentId: frameId, ...element });
  return {
    result: { id, title, scene },
    text(key, text, x, y, width, height, options = {}) {
      add(key, { type: 'text', text, x, y, width, height, fontFamily: FONT,
        fontSize: 20, fontWeight: 400, color: INK, textAlign: 'left', ...options });
    },
    box(key, x, y, width, height, fillColor, options = {}) {
      add(key, { type: 'shape', shapeType: 'rect', x, y, width, height,
        fillColor, strokeColor: fillColor, strokeWidth: 0, radius: 0, ...options });
    },
    image(key, file, x, y, width, height, caption) {
      const assetId = `${id}-${key}-asset`;
      scene.assets.push({ id: assetId, mime: file.endsWith('.webp') ? 'image/webp' : 'image/png', href: `${ASSET_ROOT}/${file}` });
      add(key, { type: 'image', assetId, x, y, width, height, caption });
    },
  };
}

function fractionBar(p, id, x, y, width, height, parts, colors) {
  const gap = 3;
  const cellWidth = (width - gap * (parts - 1)) / parts;
  for (let index = 0; index < parts; index++) {
    p.box(`${id}-part-${index + 1}`, x + index * (cellWidth + gap), y, cellWidth, height,
      colors[index] || '#ffffff', { strokeColor: '#91b7b4', strokeWidth: 1.2, radius: 0.06 });
  }
}

function learningHeader(p, number, title, subtitle) {
  p.box('top-rule', 52, 43, 656, 2, TEAL);
  p.text('kicker', 'EDUSENSE  /  초등 수학 · 분수의 덧셈', 52, 57, 590, 25,
    { fontSize: 14, color: TEAL, fontWeight: 700 });
  p.text('page-number', number, 654, 57, 54, 25, { fontSize: 14, color: TEAL, textAlign: 'right' });
  p.text('title', title, 52, 99, 472, 118, { fontSize: 38, fontWeight: 700 });
  p.image('hero', 'fraction-tiles-hands.webp', 524, 114, 190, 127, '분수 조각을 손으로 맞추는 활동 그림');
  p.text('subtitle', subtitle, 52, 231, 650, 29, { fontSize: 18 });
}

function learningFooter(p, number) {
  p.box('footer-rule', 52, 1024, 656, 1, '#b4cfca');
  p.text('footer', '기존 분수의 덧셈 콘텐츠 · 편집 가능한 학습지 샘플', 52, 1035, 600, 23,
    { fontSize: 13, color: '#547478' });
  p.text('footer-number', number, 666, 1035, 42, 23, { fontSize: 13, color: '#547478', textAlign: 'right' });
}

function fractionPages() {
  const first = page('fraction-concept', '분수의 덧셈 · 개념과 풀이', '#fbfaf4');
  learningHeader(first, '01', '분모가 다르면,\n크기부터 맞춰요', '반 조각과 삼분의 일 조각을 더하려면 어떻게 해야 할까요?');
  const concepts = [
    ['분모', '같은 크기로 나눈\n전체 조각의 수예요.'],
    ['분자', '나눈 조각 중에서\n선택한 조각의 수예요.'],
    ['통분', '분수의 값은 그대로,\n분모를 같게 만들어요.'],
  ];
  concepts.forEach(([title, body], index) => {
    const x = 52 + index * 224;
    first.box(`concept-${index}-card`, x, 288, 208, 154, '#ffffff', { strokeColor: '#bfd8d0', strokeWidth: 1, radius: 0.08 });
    first.text(`concept-${index}-title`, title, x + 20, 309, 168, 34, { fontSize: 24, fontWeight: 700, color: TEAL });
    first.text(`concept-${index}-body`, body, x + 20, 359, 174, 60, { fontSize: 17 });
  });
  first.text('diagram-heading', '같은 한 판을 6등분하면', 52, 478, 650, 38, { fontSize: 26, fontWeight: 700 });
  first.box('diagram-card', 52, 529, 656, 347, '#edf5ef', { radius: 0.06 });
  first.text('half-label', '1/2 = 3/6', 77, 556, 170, 38, { fontSize: 25, fontWeight: 700, color: TEAL });
  fractionBar(first, 'half', 281, 552, 396, 48, 6, [TEAL, TEAL, TEAL]);
  first.text('half-note', '분자와 분모에 3을 곱해요.', 281, 609, 398, 28, { fontSize: 16, color: '#547478' });
  first.text('third-label', '1/3 = 2/6', 77, 663, 170, 38, { fontSize: 25, fontWeight: 700, color: '#b66552' });
  fractionBar(first, 'third', 281, 659, 396, 48, 6, [CORAL, CORAL]);
  first.text('third-note', '분자와 분모에 2를 곱해요.', 281, 716, 398, 28, { fontSize: 16, color: '#547478' });
  first.box('sum-rule', 77, 760, 606, 1, '#b4cfca');
  first.text('sum-label', '3/6 + 2/6', 77, 785, 187, 36, { fontSize: 25, fontWeight: 700 });
  fractionBar(first, 'sum', 281, 782, 396, 48, 6, [TEAL, TEAL, TEAL, CORAL, CORAL]);
  first.text('sum-result', '= 5/6', 541, 838, 136, 30, { fontSize: 21, fontWeight: 700, textAlign: 'right' });
  first.box('summary-card', 52, 908, 656, 84, '#faedcc', { radius: 0.08 });
  first.text('summary', '같은 크기로 나눈 뒤, 조각 수를 더해요.', 77, 933, 606, 40,
    { fontSize: 25, fontWeight: 700, textAlign: 'center' });
  learningFooter(first, '1 / 2');

  const second = page('fraction-practice', '분수의 덧셈 · 연습과 확인', '#fbfaf4');
  learningHeader(second, '02', '같은 크기로 나누고,\n내 힘으로 더해요', '공통 분모를 정하고, 분자끼리 더한 이유도 설명해 보세요.');
  const exercises = [
    { id: 'first', y: 291, title: '01  1/3 + 1/4', parts: 12, label: '12등분한 한 판에 4조각과 3조각을 표시해요.' },
    { id: 'second', y: 552, title: '02  2/3 + 1/6', parts: 6, label: '6등분한 한 판에 4조각과 1조각을 표시해요.' },
  ];
  for (const exercise of exercises) {
    const { id, y, title, parts, label } = exercise;
    second.box(`${id}-card`, 52, y, 656, 233, '#ffffff', { strokeColor: '#bfd8d0', strokeWidth: 1, radius: 0.06 });
    second.text(`${id}-title`, title, 77, y + 20, 590, 44, { fontSize: 29, fontWeight: 700 });
    second.text(`${id}-instruction`, label, 77, y + 77, 596, 32, { fontSize: 18 });
    fractionBar(second, `${id}-practice`, 77, y + 121, 606, 43, parts, []);
    second.text(`${id}-answer-label`, '계산과 설명', 77, y + 184, 114, 30, { fontSize: 17, color: TEAL, fontWeight: 700 });
    second.box(`${id}-answer-line`, 211, y + 212, 472, 1, '#91b7b4');
  }
  second.box('mistake-card', 52, 818, 656, 123, '#faedcc', { radius: 0.08 });
  second.text('mistake-title', '왜 1/2 + 1/3은 2/5가 아닐까요?', 76, 837, 608, 36, { fontSize: 23, fontWeight: 700 });
  second.text('mistake-explanation', '분모까지 더하면 조각의 크기가 달라져요.\n먼저 분모를 같게 만든 다음, 분자끼리 더해요.', 76, 885, 608, 52, { fontSize: 18 });
  second.text('answers', '답 확인  ① 4/12 + 3/12 = 7/12    ② 4/6 + 1/6 = 5/6', 52, 971, 656, 30,
    { fontSize: 17, color: '#547478' });
  learningFooter(second, '2 / 2');
  return [first.result, second.result];
}

function sajuHeader(p, number, title, subtitle) {
  p.box('page-border', 28, 28, 704, 1022, NAVY, { strokeColor: GOLD, strokeWidth: 1.2 });
  p.text('kicker', 'SAJU  /  재물 성향 안내 · 편집 연습용 샘플', 56, 53, 625, 27,
    { fontSize: 14, color: GOLD });
  p.text('title', title, 56, 102, 650, 117,
    { fontSize: 39, fontFamily: 'Content KoPub Batang', fontWeight: 700, color: CREAM });
  p.text('subtitle', subtitle, 58, 237, 644, 51, { fontSize: 18, color: '#e1d9c6' });
  p.text('footer', '기존 예시 콘텐츠 재구성 · 독자 개인의 분석 결과가 아닙니다.', 57, 1010, 600, 24,
    { fontSize: 13, color: '#d5c394' });
  p.text('footer-number', number, 650, 1010, 53, 24, { fontSize: 13, color: GOLD, textAlign: 'right' });
}

function sajuPages() {
  const first = page('saju-wealth-traits', '재물 성향 안내 · 네 가지 관점', NAVY);
  sajuHeader(first, '1 / 2', '돈을 만드는 힘과,\n내 몫으로 남기는 구조', '기존 재물운 편집 샘플의 성향과 관리 기준을 읽어 봅니다.');
  const cards = [
    ['꾸준 축적형', '한 번에 크게보다\n작게 검증하며\n쌓아 가는 흐름', 'wealth-coins.png'],
    ['결과 가치형', '한 일의 양보다\n만든 결과의\n쓰임을 봅니다', 'wealth-chart.png'],
    ['지식 수익형', '기술·정보·콘텐츠를\n쓸모 있는 결과로\n연결합니다', 'wealth-book.png'],
    ['계약 관리형', '범위·시간·비용을\n분명히 할 때\n내 몫이 남습니다', 'wealth-shield-check.png'],
  ];
  cards.forEach(([title, body, image], index) => {
    const x = 56 + (index % 2) * 336;
    const y = 310 + Math.floor(index / 2) * 279;
    first.box(`trait-${index}-card`, x, y, 312, 255, CREAM, { strokeColor: GOLD, strokeWidth: 1, radius: 0.04 });
    first.image(`trait-${index}-art`, image, x + 23, y + 23, 76, 76, `${title}을 표현하는 장식 아이콘`);
    first.text(`trait-${index}-title`, title, x + 109, y + 49, 184, 42,
      { fontSize: 24, fontWeight: 700, color: NAVY });
    first.box(`trait-${index}-rule`, x + 24, y + 113, 264, 1, '#d3bc8c');
    first.text(`trait-${index}-body`, body, x + 25, y + 141, 265, 89,
      { fontSize: 21, color: NAVY });
  });
  first.text('summary', '바쁘게 일하는 것과 돈이 남는 것은 다릅니다.\n내가 만든 가치만큼, 맡는 범위도 분명해야 합니다.', 57, 900, 646, 71,
    { fontSize: 22, fontWeight: 700, color: CREAM, textAlign: 'center' });

  const second = page('saju-wealth-practice', '재물 성향 안내 · 관리의 기준', NAVY);
  sajuHeader(second, '2 / 2', '바쁜 일과 남는 몫을\n따로 살펴보세요', '전통 명리의 상징 해설을 실제 경험과 대조하는 예시입니다.');
  const wealth = SAJU_PRODUCT_COPY['wealth-flow'];
  const pictures = ['wealth-wallet.png', 'wealth-repeat.png', 'wealth-shield-alert.png'];
  wealth.beats.forEach((beat, index) => {
    const y = 310 + index * 170;
    second.box(`step-${index}-card`, 56, y, 648, 147, CREAM, { strokeColor: GOLD, strokeWidth: 1, radius: 0.04 });
    second.image(`step-${index}-art`, pictures[index], 77, y + 32, 84, 84, `${beat.title}을 표현하는 장식 아이콘`);
    second.text(`step-${index}-title`, `${index + 1}  ${beat.title}`, 181, y + 20, 497, 39,
      { fontSize: 24, fontWeight: 700, color: NAVY });
    second.text(`step-${index}-body`, beat.text, 181, y + 69, 492, 66, { fontSize: 18, color: NAVY });
  });
  second.box('advice-rule', 57, 838, 646, 1, GOLD);
  second.text('advice-title', '자기 점검으로 이어가기', 57, 860, 646, 35, { fontSize: 23, fontWeight: 700, color: GOLD });
  second.text('advice', wealth.advice, 57, 913, 646, 80, { fontSize: 20, color: CREAM });
  return [first.result, second.result];
}

const samples = [
  {
    id: 'penpot-learning-fractions', editor: 'penpot', mode: 'edgeless', family: 'learning-editorial',
    title: '분수 학습지 · Penpot', description: '분수 막대와 설명, 연습 문제를 직접 고치는 두 쪽 학습지',
    pages: fractionPages(),
  },
  {
    id: 'penpot-saju-wealth-guide', editor: 'penpot', mode: 'edgeless', family: 'saju-editorial',
    title: '사주 성향 안내 · Penpot', description: '재물 성향을 설명하는 글·카드·그림을 직접 고치는 두 쪽 안내서',
    pages: sajuPages(),
  },
  {
    id: 'blocksuite-learning-notes', editor: 'blocksuite', mode: 'page', family: 'learning-editorial',
    title: '학습노트 · BlockSuite 문서', description: '배운 내용을 자기 말로 정리하며 제목과 본문을 이어 쓰는 문서',
    content: {
      schemaVersion: 1, templateId: 'learning-editorial', title: '설명을 내 것으로 만드는 학습노트',
      kicker: 'EDUSENSE · 나의 설명을 완성하는 학습노트',
      subtitle: '읽은 내용을 자기 말로 설명하고, 빠진 부분을 확인합니다.',
      footer: '학습 주제에 맞춰 개념, 사례와 확인 질문을 바꿔 사용하세요.',
      sections: [
        { id: 'notes-overview', kind: 'text', title: '오늘의 질문',
          body: '오늘 읽을 내용에서 궁금한 질문 하나를 고릅니다.\n\n내가 설명하려는 질문: ' },
        { id: 'notes-method', kind: 'steps', title: '설명을 만드는 세 단계', items: [
          { title: '1. 핵심 찾기', body: '이 내용이 답하려는 질문을 한 문장으로 적습니다.' },
          { title: '2. 내 말로 설명', body: '자료를 덮고, 핵심 개념과 이유를 연결해 말합니다.' },
          { title: '3. 다시 확인', body: '막힌 부분을 원문에서 찾아 설명을 고칩니다.' },
        ] },
        { id: 'notes-practice', kind: 'exercise', title: '직접 써 보기',
          body: '지금 배우는 개념 하나를 고르고, 처음 듣는 사람에게 설명해 보세요.', items: [
            { title: '개념', body: '무엇을 뜻하는 말인가요?\n\n내 설명: ' },
            { title: '사례', body: '어떤 상황에서 사용할 수 있나요?\n\n떠올린 사례: ' },
            { title: '확인', body: '설명을 확인한 자료의 어느 부분이 근거인가요?\n\n다시 확인할 부분: ' },
          ] },
        { id: 'notes-next', kind: 'text', title: '설명하다 멈춘 곳이 다음 학습의 출발점',
          body: '뜻을 안다고 느꼈던 단어도 다른 사람에게 설명하려면 더 분명한 표현이 필요합니다. 설명이 이어지지 않는 부분에 표시하고, 원문의 정의나 사례와 비교해 보세요.' },
        { id: 'notes-summary', kind: 'quote', title: '오늘 남길 한 문장',
          body: '잘 설명되는 부분과 다시 확인할 부분을 구분해 보세요.\n\n오늘의 한 문장: ' },
      ],
    },
  },
];

export const EDITOR_SAMPLE_PRESETS = Object.freeze(samples.map(sample => Object.freeze({
  id: sample.id, editor: sample.editor, mode: sample.mode, family: sample.family, title: sample.title,
  label: sample.title, description: sample.description, pageCount: sample.pages ? sample.pages.length : 1,
})));

/** Every insertion receives independent scene objects or editorial content. */
export function getEditorSample(id) {
  const sample = samples.find(candidate => candidate.id === id);
  return sample ? structuredClone(sample) : null;
}
